import React, { useState, useEffect } from 'react';
import { calculateCardPosition } from '../../utils/seatPositioning';

const suitSymbols = { spades: "♠", hearts: "♥", diamonds: "♦", clubs: "♣" };
const suitColors = { spades: "#333", clubs: "#333", hearts: "#d22", diamonds: "#d22" };

/**
 * PlayerHand Component
 * Renders 2 cards on the felt, positioned relative to the seat
 */
export default function PlayerHand({ 
  hand, 
  seatPosition, 
  seatAngle, 
  tableCenter, 
  cardVisibility,
  phase,
  isLocalPlayer,
  revealedIndex = -1 // For reveal animation
}) {
  if (!hand || hand.length !== 2) {
    if (isLocalPlayer) {
      console.log(`DEBUG: PlayerHand - No hand or wrong length. hand:`, hand, `cardVisibility:`, cardVisibility);
    }
    return null;
  }
  if (!cardVisibility.showCards) {
    if (isLocalPlayer) {
      console.log(`DEBUG: PlayerHand - showCards is false. cardVisibility:`, cardVisibility);
    }
    return null;
  }
  
  // Debug logging for local player
  if (isLocalPlayer) {
    console.log(`DEBUG: PlayerHand - Rendering cards for local player. hand:`, hand, `seatPosition:`, seatPosition, `tableCenter:`, tableCenter, `cardVisibility:`, cardVisibility);
  }
  
  return (
    <>
      {hand.map((card, cardIndex) => {
        const cardPos = calculateCardPosition(
          seatPosition.x,
          seatPosition.y,
          seatAngle,
          cardIndex,
          tableCenter.x,
          tableCenter.y
        );
        
        // Debug logging for local player
        if (isLocalPlayer) {
          console.log(`DEBUG: PlayerHand - Card ${cardIndex} position:`, cardPos, `card:`, card);
        }
        
        const shouldShowFaceUp = cardVisibility.showFaceUp || 
          (phase === 'reveal' && revealedIndex >= cardIndex) ||
          (phase === 'results' && cardVisibility.showFaceUp);
        
        return (
          <Card
            key={cardIndex}
            card={card}
            position={cardPos}
            faceUp={shouldShowFaceUp}
            isLocalPlayer={isLocalPlayer}
            revealDelay={cardIndex * 150} // Stagger reveal animation
            cardIndex={cardIndex} // Pass cardIndex for z-index layering
          />
        );
      })}
    </>
  );
}

/**
 * Card Component
 * Renders a single playing card (face up or face down)
 */
function Card({ card, position, faceUp, isLocalPlayer, revealDelay = 0, cardIndex = 0 }) {
  const [isFlipping, setIsFlipping] = useState(false);
  // For local player cards, show immediately without flip animation
  // Initialize isRevealed based on faceUp - if faceUp is true, show immediately
  const [isRevealed, setIsRevealed] = useState(faceUp);
  
  useEffect(() => {
    // If faceUp changes to true and we're not revealed, show immediately for local player
    // For opponents, use flip animation
    if (faceUp && !isRevealed) {
      if (isLocalPlayer) {
        // Local player: show immediately, no animation
        setIsRevealed(true);
      } else {
        // Opponent: use flip animation
        setIsFlipping(true);
        setTimeout(() => {
          setIsRevealed(true);
          setTimeout(() => setIsFlipping(false), 200);
        }, 200 + revealDelay);
      }
    } else if (!faceUp && isRevealed) {
      // If faceUp becomes false, hide the card
      setIsRevealed(false);
    }
  }, [faceUp, isRevealed, revealDelay, isLocalPlayer]);
  
  // Debug logging for local player
  if (isLocalPlayer) {
    console.log(`DEBUG: Card component - Rendering card at position:`, position, `faceUp:`, faceUp, `isRevealed:`, isRevealed, `isLocalPlayer:`, isLocalPlayer);
  }
  
  return (
    <div
      style={{
        position: 'absolute',
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: `translate(-50%, -50%) rotate(${position.rotation}deg)`,
        width: '50px', // Increased by 5% (48px * 1.05 = 50.4px, rounded to 50px)
        height: '70px', // Increased by 5% (67px * 1.05 = 70.35px, rounded to 70px)
        perspective: '1000px',
        zIndex: isLocalPlayer ? (20 + cardIndex) : (10 + cardIndex), // Right card (cardIndex 1) on top of left card (cardIndex 0)
        pointerEvents: 'none', // Prevent cards from blocking clicks
        opacity: 1, // Ensure cards are visible
        visibility: 'visible' // Ensure cards are visible
      }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          transformStyle: 'preserve-3d',
          transition: isFlipping ? 'transform 0.4s ease-in-out' : 'none',
          // When revealed, rotate to show face (180deg). When not revealed, show back (0deg)
          transform: isRevealed ? 'rotateY(180deg)' : 'rotateY(0deg)'
        }}
      >
        {/* Card back - always present, shown when not revealed */}
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
            border: '2px solid #1e3a8a',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            backfaceVisibility: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: 'rotateY(0deg)'
          }}
        >
          <div
            style={{
              fontSize: '1.6rem', // Reduced by 20% (2rem * 0.8)
              color: 'white',
              opacity: 0.8
            }}
          >
            🎴
          </div>
        </div>
        
        {/* Card face - always present, shown when revealed */}
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: 'white',
            border: '2px solid #333',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            display: 'flex',
            flexDirection: 'column',
            padding: '5px', // Reduced by 20%
            boxSizing: 'border-box',
            overflow: 'hidden' // Prevent content from overflowing card bounds
          }}
        >
            {/* Top rank and suit */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                fontSize: '0.96rem', // Reduced by 20% (1.2rem * 0.8)
                fontWeight: '700',
                color: suitColors[card.suit] || '#333',
                lineHeight: '1',
                flexShrink: 0
              }}
            >
              <div>{card.rank}</div>
              <div style={{ fontSize: '0.8rem' }}>{suitSymbols[card.suit] || card.suit}</div>
            </div>
            
            {/* Center suit (large) */}
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '2rem', // Reduced by 20% (2.5rem * 0.8)
                color: suitColors[card.suit] || '#333',
                minHeight: 0 // Allow flex item to shrink
              }}
            >
              {suitSymbols[card.suit] || card.suit}
            </div>
            
            {/* Bottom rank and suit (on opposite corner - bottom right) */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                fontSize: '0.96rem', // Reduced by 20% (1.2rem * 0.8)
                fontWeight: '700',
                color: suitColors[card.suit] || '#333',
                lineHeight: '1',
                flexShrink: 0,
                marginTop: 'auto' // Push to bottom
              }}
            >
              <div style={{ transform: 'rotate(180deg)' }}>{card.rank}</div>
              <div style={{ fontSize: '0.8rem', transform: 'rotate(180deg)' }}>{suitSymbols[card.suit] || card.suit}</div>
            </div>
        </div>
      </div>
    </div>
  );
}

